import React, { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  Select,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";

export const FaqList = () => {
  const [language, setLanguage] = useState("en");
  const [faqContent, setFaqContent] = useState([]);
  const [editableFaqContent, setEditableFaqContent] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const toast = useToast();

  // Fetch FAQ content for the selected language
  const fetchFaqContent = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL3}/admin/faq/${language}`;
      const response = await fetchWithToken(url);
      if (!response) throw new Error("Failed to fetch FAQ content");
      setFaqContent(response.content);
      setEditableFaqContent(response.content);
      setIsEditMode(false);
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to fetch FAQ content",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchFaqContent();
  }, [language]);

  // Handle input changes in edit mode
  const handleInputChange = (catIdx, convIdx, field, value) => {
    const updated = [...editableFaqContent];
    updated[catIdx].conversation[convIdx][field] = value;
    setEditableFaqContent(updated);
  };

  // Submit updates
  const handleSubmit = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL3}/admin/faq/${language}`;
      const payload = { language, content: editableFaqContent };
      const response = await fetchWithToken(url,"PUT", payload);
      if (!response) throw new Error("Failed to update FAQ content");
      toast({
        title: "Success",
        description: "FAQ updated successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      fetchFaqContent();
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to update FAQ content",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setEditableFaqContent(faqContent);
    setIsEditMode(false);
  };

  // Filter content based on search query
  const filteredContent = editableFaqContent
    .map((cat) => {
      const filteredConv = cat.conversation.filter((c) => {
        const q = c.question.toLowerCase();
        const a = c.answer.toLowerCase();
        const sq = searchQuery.toLowerCase();
        return q.includes(sq) || a.includes(sq);
      });
      return filteredConv.length
        ? { ...cat, conversation: filteredConv }
        : null;
    })
    .filter(Boolean);

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }}>
      <Flex px="25px" justify="space-between" mb="20px" align="center" wrap="wrap" gap="20px">
        <Text color="gray.800" fontSize="22px" fontWeight="700" lineHeight="100%" flexBasis="100%">
          FAQ List
        </Text>
        <Flex gap="10px" flexWrap="wrap" align="center">
          <Select
            placeholder="Select Language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            size="sm"
            w={{ base: "100%", md: "200px" }}
          >
            <option value="en">English</option>
            <option value="it">Italian</option>
            <option value="fr">French</option>
          </Select>
          <Input
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="sm"
            w={{ base: "100%", md: "200px" }}
          />
          {!isEditMode ? (
            <Button size="sm" colorScheme="blue" onClick={() => setIsEditMode(true)}>
              Edit
            </Button>
          ) : (
            <>
              <Button size="sm" colorScheme="red" onClick={handleCancel} mr="8px">
                Cancel
              </Button>
              <Button size="sm" colorScheme="green" onClick={handleSubmit}>
                Save Changes
              </Button>
            </>
          )}
        </Flex>
      </Flex>

      <Box overflowY="auto" maxHeight="400px">
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead>
            <Tr bg="rgb(69, 50, 240)">
              <Th textColor="white">Category</Th>
              <Th textColor="white">Question</Th>
              <Th textColor="white">Answer</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredContent.map((cat, catIdx) =>
              cat.conversation.map((conv, convIdx) => (
                <Tr key={`${catIdx}-${convIdx}`}>
                  <Td>
                    {convIdx === 0 ? (
                      <Input
                        size="sm"
                        value={cat.category}
                        isDisabled={!isEditMode}
                        onChange={(e) => handleInputChange(catIdx, convIdx, "category", e.target.value)}
                      />
                    ) : null}
                  </Td>
                  <Td>
                    <Input
                      size="sm"
                      value={conv.question}
                      isDisabled={!isEditMode}
                      onChange={(e) => handleInputChange(catIdx, convIdx, "question", e.target.value)}
                    />
                  </Td>
                  <Td>
                    <Input
                      size="sm"
                      value={conv.answer}
                      isDisabled={!isEditMode}
                      onChange={(e) => handleInputChange(catIdx, convIdx, "answer", e.target.value)}
                    />
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
};
